import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AccountService, CommentService, FilesService } from '@core/services';
import { environment } from '@env/environment';
import { ChangableComponent } from '@models/changable.component';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { ImageSizeEnum, IMeetingComplaint, Meeting, MeetingTypeEnum, User, UserService } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCoverModule, OnUiTextareaModule } from 'onclass-ui';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-complaint-meeting-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OnUiCoverModule,
    TranslateModule,
    OnUiTextareaModule,
    OnUiButtonModule,
    RouterModule,
    IconComponent,
  ],
  templateUrl: './complaint-meeting-form.component.html',
  styleUrls: ['./complaint-meeting-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplaintMeetingFormComponent extends ChangableComponent implements OnInit {
  @Input() meeting: Meeting | UserService;
  @Input() author: User;

  public cover: OnUiCover;
  public avatar: OnUiCover;
  public subject: string;
  public authorFullName: string;
  public complainSent: boolean;
  public complainError: boolean;
  public meetingServiceType = MeetingTypeEnum;
  public emailCheckLink = environment.default_host + '/profile/contacts';
  public meetingComplaint: Partial<IMeetingComplaint> = {
    comment: '',
  };

  constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    public commentService: CommentService,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  ngOnInit() {
    this.cover = {
      type: 'cover',
      img: FilesService.getFileUrlBySize(this.meeting.cover_id, ImageSizeEnum.wide),
      title: this.meeting.title,
    };
    this.subject = this.meeting.subject ? this.meeting.subject.title : null;

    this.authorFullName = AccountService.getUserFullName(this.author);
    this.avatar = {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(this.author?.userpic_id),
      title: this.authorFullName,
      form: 'circle',
    };
  }

  public get type() {
    switch (this.meeting.type) {
      case MeetingTypeEnum.single:
        return 'meetings.types.single';
      case MeetingTypeEnum.group:
        return 'meetings.types.group';
      case MeetingTypeEnum.webinar:
        return 'meetings.types.webinar';
      case MeetingTypeEnum.talk:
        return 'meetings.types.talk';
      case MeetingTypeEnum.stream:
        return 'meetings.types.stream';
      default:
        return '';
    }
  }

  public reset() {
    this.complainSent = false;
    this.complainError = false;
  }

  public submitComplaint() {
    this.meetingComplaint.meeting_owner_id = this.author.id;
    this.meetingComplaint.meeting_id = this.meeting.id;
    return this.commentService
      .createMeetingComplaint(this.meetingComplaint)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.complainSent = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          this.complainError = true;
          this.detectChanges();
        },
      });
  }
}
